import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiShield, FiLock, FiUser, FiEye, FiEyeOff, FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import api from "../../services/api";

export default function SuperAdminLogin() {
    const navigate = useNavigate();
    const { loginSuccess } = useApp();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username.trim() || !password) {
            toast.error("Username and password are required.");
            return;
        }

        setLoading(true);
        try {
            const res = await api.post("/auth/login", {
                username: username.trim().toLowerCase(),
                password,
                isSuperAdminPortal: true,
            });

            const { token, user } = res.data;

            if (user.role !== "superadmin") {
                toast.error("Access denied. This portal is reserved for Super Administrators.");
                return;
            }

            loginSuccess(token, user);
            toast.success(`Welcome back, ${user.displayName || user.username}!`);
            navigate("/superadmin", { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.message || "Super Admin authentication failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #090e17 0%, #0f172a 50%, #1e293b 100%)",
            color: "#f8fafc",
            padding: "1.5rem",
            position: "relative",
            overflow: "hidden",
            fontFamily: "Inter, -apple-system, sans-serif"
        }}>
            {/* Ambient background glow */}
            <div style={{
                position: "absolute",
                top: "20%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 500,
                height: 500,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(176,141,69,0.12) 0%, rgba(15,23,42,0) 70%)",
                pointerEvents: "none"
            }} />

            <div style={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1 }}>
                {/* Brand Header */}
                <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                    <div style={{
                        width: 64,
                        height: 64,
                        borderRadius: 18,
                        background: "linear-gradient(135deg, #B08D45 0%, #D4AF37 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 1.25rem",
                        boxShadow: "0 8px 24px rgba(176,141,69,0.35)",
                        color: "#fff"
                    }}>
                        <FiShield size={30} />
                    </div>

                    <span style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        background: "rgba(176,141,69,0.2)",
                        color: "#f7d583",
                        padding: "3px 10px",
                        borderRadius: 20,
                        border: "1px solid rgba(176,141,69,0.4)",
                        display: "inline-block",
                        marginBottom: 10
                    }}>
                        CIGOI Central Governance
                    </span>

                    <h1 style={{ fontSize: 22, fontWeight: 800, color: "#ffffff", letterSpacing: "-0.02em", marginBottom: 6 }}>
                        Super Admin Cockpit
                    </h1>
                    <p style={{ fontSize: 13, color: "#94a3b8", maxWidth: 360, margin: "0 auto", lineHeight: 1.5 }}>
                        Executive command &amp; multi-college management access
                    </p>
                </div>

                {/* Login Card */}
                <div style={{
                    background: "rgba(30, 41, 59, 0.8)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 16,
                    padding: "2.25rem",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.4)"
                }}>
                    <form onSubmit={handleSubmit}>
                        {/* Username */}
                        <div style={{ marginBottom: "1.25rem" }}>
                            <label style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#cbd5e1", marginBottom: 6 }}>
                                Executive Username
                            </label>
                            <div style={{ position: "relative" }}>
                                <FiUser size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
                                <input
                                    type="text"
                                    placeholder="Enter superadmin username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    autoFocus
                                    autoComplete="username"
                                    style={{
                                        width: "100%",
                                        padding: "10px 14px 10px 40px",
                                        background: "rgba(15, 23, 42, 0.6)",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        borderRadius: 8,
                                        color: "#fff",
                                        fontSize: 14,
                                        outline: "none",
                                        boxSizing: "border-box"
                                    }}
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div style={{ marginBottom: "1.75rem" }}>
                            <label style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#cbd5e1", marginBottom: 6 }}>
                                Master Password
                            </label>
                            <div style={{ position: "relative" }}>
                                <FiLock size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
                                <input
                                    type={showPw ? "text" : "password"}
                                    placeholder="••••••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    style={{
                                        width: "100%",
                                        padding: "10px 42px 10px 40px",
                                        background: "rgba(15, 23, 42, 0.6)",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        borderRadius: 8,
                                        color: "#fff",
                                        fontSize: 14,
                                        outline: "none",
                                        boxSizing: "border-box"
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((p) => !p)}
                                    style={{
                                        position: "absolute",
                                        right: 12,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none",
                                        border: "none",
                                        color: "#94a3b8",
                                        cursor: "pointer",
                                        padding: 4,
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                    tabIndex={-1}
                                >
                                    {showPw ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                width: "100%",
                                padding: "12px",
                                background: "linear-gradient(135deg, #003b67 0%, #0284c7 100%)",
                                border: "none",
                                borderRadius: 8,
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 14,
                                cursor: loading ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                                boxShadow: "0 4px 14px rgba(2,132,199,0.3)",
                                transition: "all 0.15s ease"
                            }}
                            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.filter = "brightness(1.1)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
                        >
                            {loading ? "Authenticating Master Access…" : <>Sign In to Cockpit <FiArrowRight size={16} /></>}
                        </button>
                    </form>
                </div>

                {/* Back to college portals */}
                <div style={{ textAlign: "center", marginTop: "1.75rem" }}>
                    <Link
                        to="/"
                        style={{
                            color: "#94a3b8",
                            fontSize: 13,
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            transition: "color 0.15s"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = "#f8fafc"}
                        onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}
                    >
                        <FiArrowLeft size={14} /> Back to College Directory
                    </Link>
                </div>
            </div>
        </div>
    );
}
